import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators';
import AppointmentRecurrenceApi from '@/api/appointment-recurrences-api';
import dayjs from 'dayjs';
import _ from 'lodash';
import { installationsModule } from './store-accessor';
import { showRecurrenceDaysOptions } from '@/helpers/data';
import { hasAssigneeWithoutName, normalizeAssigneeIdsForSubmit } from '@/helpers/assignees';

@Module({ namespaced: true, name: "appointmentsRecurrences" })
export default class AppointmentsRecurrencesModule extends VuexModule {

  @Action
  async getAppointmentRecurrenceInfoById(recurrenceId: string): Promise<any> { // TODO: Add type
    const appointmentRecurrenceApi = new AppointmentRecurrenceApi();
    const recurrenceDetails = await appointmentRecurrenceApi.getAppointmentRecurrencesById(recurrenceId);

    return recurrenceDetails;
  }

  @Action
  async createAppointmentRecurrenceExceptionAction(data: Record<string, any>) {
    const appointmentRecurrenceApi = new AppointmentRecurrenceApi();
    data.custom_appointment_data.assignee_ids = normalizeAssigneeIdsForSubmit(data.custom_appointment_data.assignee_ids);
    if (data.custom_appointment_data.installation_item_id && data.custom_appointment_data.installation_item_id.id) {
        data.custom_appointment_data.item_type_text = data.custom_appointment_data.installation_item_id.item_type_text;
        data.custom_appointment_data.item_make = data.custom_appointment_data.installation_item_id.item_make;
        data.custom_appointment_data.item_model = data.custom_appointment_data.installation_item_id.item_model;
        data.custom_appointment_data.item_serial_no = data.custom_appointment_data.installation_item_id.item_serial_no;
        data.custom_appointment_data.installation_item_id = data.custom_appointment_data.installation_item_id.id;
      } else {
        delete data.custom_appointment_data.installation_item_id;
      }

      data.custom_appointment_data.notes = data.custom_appointment_data.appointment_notes ? data.custom_appointment_data.appointment_notes : data.custom_appointment_data.notes;
      data.custom_appointment_data.duration = data.custom_appointment_data.duration.dur;


      if(!dayjs(data.custom_appointment_data.scheduled_start, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]', true).isValid() && data.custom_appointment_data.scheduled_start_time) {
        data.custom_appointment_data.scheduled_start = data.custom_appointment_data.scheduled_start_time ? dayjs(data.custom_appointment_data.scheduled_start + ' ' + data.custom_appointment_data.scheduled_start_time).toISOString() : null;
      } else {
        // Cancel appointment
        data.custom_appointment_data.scheduled_start = dayjs(data.custom_appointment_data.scheduled_start).toISOString();
      }
      delete data.custom_appointment_data.scheduled_start_time;

      if(data.custom_appointment_data.type_id?.id) {
        data.custom_appointment_data.action_type_id = data.custom_appointment_data.type_id.id;
      } // else, type_id is not an object (cancel modal)

      await appointmentRecurrenceApi.createAppointmentRecurrenceException(data);
      if(data.custom_appointment_data?.installation_id) {
        await installationsModule.getInstallationDetailsAppointments(data.custom_appointment_data?.installation_id);
        await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'tasks', 'items'], installationId: data.custom_appointment_data.installation_id});
      }
  }

  @Action
  async deleteAppointmentRecurrenceOccurrence(data: Record<string, any>) {
    const appointmentRecurrenceApi = new AppointmentRecurrenceApi();

    await appointmentRecurrenceApi.deleteAppointmentRecurrenceOccurrence(data);

    if (data.installation_id) {
      await installationsModule.getInstallationDetailsAppointments(data.installation_id);
      await installationsModule.invalidateInstallationTab({ tabs: ['jobs'], installationId: data.installation_id });
    }
  }

  @Action
  async deleteAppointmentRecurrenceSeries(recurrenceId: string) {
    const appointmentRecurrenceApi = new AppointmentRecurrenceApi();

    await appointmentRecurrenceApi.deleteAppointmentRecurrenceSeries(recurrenceId);
  }

  @Action
    async editRecurrentAppointmentSeries(data: Record<string, any>) {
      const dataPayload = _.cloneDeep(data);

      dataPayload.job_id = data.parent_job_id;
      dataPayload.assignee_ids = normalizeAssigneeIdsForSubmit(dataPayload.assignee_ids, dataPayload.assignees);
      if (hasAssigneeWithoutName(dataPayload.assignee_ids)) {
        return;
      }

      if(typeof dataPayload.days_of_week === 'object') {
        if(showRecurrenceDaysOptions(dataPayload)) {
          dataPayload.days_of_week = dataPayload.days_of_week.map((day: Record<string, string>) => day.value);
        } else {
          delete dataPayload.days_of_week;
        }
      }

      if(typeof data.end_type === 'object') {
        dataPayload.end_type = dataPayload.end_type.value;
      }

      if(typeof data.recurrence_type === 'object') {
        dataPayload.recurrence_type = data.recurrence_type.value;
      }

      // Parent appointment fields
      dataPayload.parent_appointment = {
        notes: dataPayload.appointment_notes,
        job_description: dataPayload.job_description,
        type_id: dataPayload.type_id.id,
        job_id: dataPayload.job_id,
        scheduled_start: dayjs(dataPayload.scheduled_start + ' ' + dataPayload.scheduled_start_time).toISOString(),

        assignee_id: data.assignee.id,
        assignee_ids: dataPayload.assignee_ids,
        assignees: dataPayload.assignees,
        duration: dataPayload.duration.dur,
      }

      if (data.installation_item_id && data.installation_item_id.id) {
        dataPayload.parent_appointment.item_type_text = dataPayload.installation_item_id.item_type_text;
        dataPayload.parent_appointment.item_make = dataPayload.installation_item_id.item_make;
        dataPayload.parent_appointment.item_model = dataPayload.installation_item_id.item_model;
        dataPayload.parent_appointment.item_serial_no = dataPayload.installation_item_id.item_serial_no;
        dataPayload.parent_appointment.installation_item_id = data.installation_item_id.id;
      } else {
        delete dataPayload.installation_item_id;
      }

      delete dataPayload.appointment_notes;
      delete dataPayload.appointment_status_id;

      const appointmentRecurrenceApi = new AppointmentRecurrenceApi();
      await appointmentRecurrenceApi.updateAppointmentRecurrenceSeries(dataPayload.id, dataPayload);
      await installationsModule.getInstallationDetailsAppointments(dataPayload.installation_id);
      await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'items'], installationId: dataPayload.installation_id});
    }

    @Action
    async endRecurringAppointment(data: Record<string, any>) {
      const appointmentData = _.cloneDeep(data);

      appointmentData.job_description = appointmentData.job_description?.trim();
      appointmentData.assignee_id = appointmentData.assignee_id.id;
      appointmentData.actions = appointmentData.type_id;
      appointmentData.payment_type_id = appointmentData.payment_type?.id;
      appointmentData.client_paid = appointmentData.radioValue == "user" ? true : false;
      appointmentData.duration = appointmentData.duration.dur;
      appointmentData.exception_date = appointmentData.actual_start || dayjs(`${appointmentData.scheduled_start_date} ${appointmentData.scheduled_start_time}`).toISOString();

      if (!appointmentData.is_paid) {
        delete appointmentData.payments;
      }

      if (appointmentData.scheduled_start_date && appointmentData.scheduled_start_time) {
        delete appointmentData.scheduled_start_date;
        delete appointmentData.scheduled_start_time;
      }

      if(!appointmentData.actual_start) {
        delete appointmentData.actual_start;
      }

      delete appointmentData.is_paid;
      delete appointmentData.radioValue;
      delete appointmentData.type_id;
      delete appointmentData.payment_type;

      appointmentData.actions.map((item: Record<string, any>) => {
        item.unit = item.is_time_charged ? item.unit.unit : null;
      });

      if(appointmentData.materials && appointmentData.materials.length > 0) {
        appointmentData.materials.map((item: Record<string, any>) => {
          item.unit = item.is_unit_based ? item.unit : null;
        });
      }

      const appointmentRecurrenceApi = new AppointmentRecurrenceApi();

      await appointmentRecurrenceApi.endRecurringAppointment(appointmentData.id, appointmentData);
      await installationsModule.getInstallationDetailsAppointments(appointmentData.installation_id);
      await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'items', 'photos'], installationId: appointmentData.installation_id});
    }
  
}
